import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { PROJECTS } from "./projectsData";
import PasswordGate, { isProjectUnlocked } from "./PasswordGate";

const normalize = (value) => String(value || "").toLowerCase();

function LockBadge() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="52"
      height="52"
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="50" cy="50" r="50" fill="white" />
      <mask
        id="lock-mask"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="30"
        y="30"
        width="40"
        height="40"
      >
        <rect x="30" y="30" width="40" height="40" fill="#D9D9D9" />
      </mask>
      <g mask="url(#lock-mask)">
        <path
          d="M39.9998 66.6665C39.0832 66.6665 38.2984 66.3401 37.6457 65.6873C36.9929 65.0346 36.6665 64.2498 36.6665 63.3332V46.6665C36.6665 45.7498 36.9929 44.9651 37.6457 44.3123C38.2984 43.6596 39.0832 43.3332 39.9998 43.3332H41.6665V39.9998C41.6665 37.6943 42.479 35.729 44.104 34.104C45.729 32.479 47.6943 31.6665 49.9998 31.6665C52.3054 31.6665 54.2707 32.479 55.8957 34.104C57.5207 35.729 58.3332 37.6943 58.3332 39.9998V43.3332H59.9998C60.9165 43.3332 61.7012 43.6596 62.354 44.3123C63.0068 44.9651 63.3332 45.7498 63.3332 46.6665V63.3332C63.3332 64.2498 63.0068 65.0346 62.354 65.6873C61.7012 66.3401 60.9165 66.6665 59.9998 66.6665H39.9998ZM52.354 57.354C53.0068 56.7012 53.3332 55.9165 53.3332 54.9998C53.3332 54.0832 53.0068 53.2984 52.354 52.6457C51.7012 51.9929 50.9165 51.6665 49.9998 51.6665C49.0832 51.6665 48.2984 51.9929 47.6457 52.6457C46.9929 53.2984 46.6665 54.0832 46.6665 54.9998C46.6665 55.9165 46.9929 56.7012 47.6457 57.354C48.2984 58.0068 49.0832 58.3332 49.9998 58.3332C50.9165 58.3332 51.7012 58.0068 52.354 57.354ZM44.9998 43.3332H54.9998V39.9998C54.9998 38.6109 54.5137 37.4304 53.5415 36.4582C52.5693 35.4859 51.3887 34.9998 49.9998 34.9998C48.6109 34.9998 47.4304 35.4859 46.4582 36.4582C45.4859 37.4304 44.9998 38.6109 44.9998 39.9998V43.3332Z"
          fill="#1C1B1F"
        />
      </g>
    </svg>
  );
}

function getProjectCardPetMessage(card) {
  const title = card.title || "";
  const lowerTitle = normalize(title);
  const lowerType = normalize(card.type);
  const lowerStatus = normalize(card.status);
  const lowerTags = (card.tags || []).map((tag) => normalize(tag));

  if (lowerTitle.includes("nukepc")) {
    return `${title} has strong builder energy.`;
  }

  if (lowerStatus === "protected" || card.locked) {
    return `${title} is hiding a few secrets.`;
  }

  if (lowerType === "case-study") {
    return `${title} feels thoughtful from a distance.`;
  }

  if (lowerTags.includes("ai")) {
    return `${title} looks like it has opinions.`;
  }

  if (lowerTags.includes("healthcare")) {
    return `${title} feels calm and important.`;
  }

  if (lowerTags.includes("books")) {
    return `${title} seems cozy in a clever way.`;
  }

  if (lowerTags.includes("gaming")) {
    return `${title} has lively screen presence.`;
  }

  if (lowerTags.includes("blog") || lowerType === "blogs") {
    return `${title} sounds like a page with a point of view.`;
  }

  return `${title} is worth a closer look.`;
}

function getProjectCardPetTalk(card) {
  const title = card.title || "";
  const lowerTitle = normalize(title);
  const lowerType = normalize(card.type);
  const lowerStatus = normalize(card.status);
  const lowerTags = (card.tags || []).map((tag) => normalize(tag));

  if (
    lowerTitle.includes("nukepc") &&
    (card.locked || lowerStatus === "protected")
  ) {
    return "This one is a little blurred because of NDA.";
  }

  if (lowerTitle.includes("nukepc")) {
    return "This one feels engineered with a lot of care.";
  }

  if (lowerTitle.includes("rmkcet")) {
    return "This redesign made the structure feel easier to trust.";
  }

  if (lowerTitle.includes("ceilang")) {
    return "This one feels soft, conversational, and smart.";
  }

  if (lowerTitle.includes("equora")) {
    return "Finance gets much nicer when the interface stops acting intimidating.";
  }

  if (lowerTitle.includes("medshift")) {
    return "High-pressure products need calm interfaces. This one understands that.";
  }

  if (lowerTitle.includes("book buddy")) {
    return "I like when discovery feels playful instead of procedural.";
  }

  if (lowerTitle.includes("spotify")) {
    return "This one has future-glass energy in a good way.";
  }

  if (lowerTitle.includes("letter peek")) {
    return "A tiny tool with good taste is still good product work.";
  }

  if (lowerTitle.includes("game")) {
    return "This one has the right amount of dashboard swagger.";
  }

  if (lowerTags.includes("ai")) {
    return "There is a lot going on here, but it still reads clearly.";
  }

  if (lowerType === "case-study") {
    return "Case-study mode. I expect decisions with reasons.";
  }

  if (card.locked || lowerStatus === "protected") {
    return "Protected work always makes me curious.";
  }

  return `${title} looks solid from here.`;
}

function shouldProjectCardSpeak(card) {
  const title = normalize(card.title);
  const status = normalize(card.status);

  if (card.locked || status === "protected") return true;
  if (title.includes("nukepc")) return true;
  if (title.includes("rmkcet")) return true;
  if (title.includes("ceilang")) return true;

  return false;
}

function getProjectCardPetPriority(card) {
  const title = normalize(card.title);
  const status = normalize(card.status);

  if (title.includes("nukepc")) return 112;
  if (card.locked || status === "protected") return 108;
  if (title.includes("rmkcet")) return 102;
  if (title.includes("ceilang")) return 98;
  return 88;
}

export default function ProjectSection({ filter = "all" }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gateProject, setGateProject] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);

    const timer = window.setTimeout(() => {
      const filtered =
        filter === "all"
          ? PROJECTS
          : PROJECTS.filter((item) => item.type === filter);

      setCards(filtered);
      setLoading(false);
    }, 450);

    return () => window.clearTimeout(timer);
  }, [filter]);

  const openProject = (card) => {
    if (!card.link) return;

    if (card.locked && !isProjectUnlocked(card.id)) {
      setGateProject(card);
      return;
    }

    navigate(card.link);
  };

  return (
    <>
      <div className="card-grid">
        {loading
          ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />)
          : cards.map((card) => {
              const isLocked = card.locked && !isProjectUnlocked(card.id);
              const allowTalk = shouldProjectCardSpeak(card);

              return (
                <article
                  key={card.id}
                  className="project-card"
                  data-platform
                  data-pet-message={getProjectCardPetMessage(card)}
                  data-pet-talk={allowTalk ? getProjectCardPetTalk(card) : ""}
                  data-pet-talk-priority={
                    allowTalk ? getProjectCardPetPriority(card) : 0
                  }
                  style={{
                    ...styles.card,
                    opacity: isLocked ? 0.72 : 1,
                    cursor: card.link ? "pointer" : "default",
                  }}
                  onClick={() => openProject(card)}
                >
                  <div style={styles.mediaWrap}>
                    {isLocked ? (
                      <img
                        src={card.lockedPreview || card.image}
                        alt=""
                        style={{ ...styles.media, filter: "blur(6px)" }}
                      />
                    ) : card.screen?.endsWith(".mp4") ? (
                      <video
                        src={card.screen}
                        autoPlay
                        muted
                        loop
                        playsInline
                        style={styles.media}
                      />
                    ) : (
                      <img
                        src={card.screen || card.image}
                        alt={card.title}
                        style={styles.media}
                      />
                    )}

                    {isLocked && (
                      <div style={styles.lockOverlay}>
                        <span style={styles.lockIcon}>
                          <LockBadge />
                        </span>
                        <span style={styles.lockOverlayText}>Protected</span>
                      </div>
                    )}
                  </div>

                  {card.tags && (
                    <div style={styles.tagWrap}>
                      <h3 style={styles.projTitle}>{card.title}</h3>
                      {card.tags.map((tag) => (
                        <span key={tag} style={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <p style={styles.projSubtitle}>{card.description}</p>

                  {isLocked && <p style={styles.lockedText}>Password required</p>}
                </article>
              );
            })}
      </div>

      {gateProject && (
        <PasswordGate
          projectId={gateProject.id}
          projectTitle={gateProject.title}
          onClose={() => setGateProject(null)}
          onUnlock={() => {
            const next = gateProject.link;
            setGateProject(null);
            navigate(next);
          }}
        />
      )}
    </>
  );
}

function SkeletonCard() {
  return (
    <div
      className="project-card"
      style={{ ...styles.card, background: "var(--portfolio-surface, #ffffff)" }}
    >
      <div style={{ ...styles.mediaWrap, ...styles.shimmer }} />
      <div style={{ ...styles.text, ...styles.shimmer }} />
      <div style={{ ...styles.textSmall, ...styles.shimmer }} />
    </div>
  );
}

const styles = {
  card: {
    padding: "16px",
    borderRadius: "12px",
    color: "var(--portfolio-page-text, #121212)",
    minHeight: "400px",
    transition: "transform 0.25s ease, opacity 0.25s ease",
    position: "relative",
  },

  mediaWrap: {
    width: "100%",
    height: "260px",
    borderRadius: "8px",
    overflow: "hidden",
    marginBottom: "10px",
    background: "#111",
    position: "relative",
  },

  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    backgroundColor: "#e6e6e6",
  },

  lockOverlay: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    background: "rgba(0,0,0,0.32)",
    color: "#fff",
    fontFamily: "'Outfit', sans-serif",
    textTransform: "uppercase",
    letterSpacing: "2px",
    fontSize: "11px",
    fontWeight: 800,
  },

  lockIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "-132px",
  },

  lockOverlayText: {
    marginTop: "38px",
  },

  tagWrap: {
    display: "flex",
    gap: "6px",
    marginBottom: "8px",
    flexWrap: "wrap",
    justifyContent: "start",
    alignItems: "center",
  },

  tag: {
    fontSize: "14px",
    padding: "3px 8px",
    background: "var(--portfolio-tag-bg, #ffffff69)",
    color: "var(--portfolio-page-text, #121212)",
    borderRadius: "9px",
    fontFamily: "Outfit, sans-serif",
    textTransform: "uppercase",
    height: "max-content",
  },

  text: {
    height: "16px",
    borderRadius: "4px",
    marginBottom: "8px",
  },

  textSmall: {
    height: "16px",
    width: "60%",
    borderRadius: "4px",
  },

  shimmer: {
    background:
      "linear-gradient(90deg, var(--portfolio-shimmer-a, #222) 25%, var(--portfolio-shimmer-b, #333) 50%, var(--portfolio-shimmer-a, #222) 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.2s infinite",
  },

  projTitle: {
    fontFamily: "PP Neue Montreal",
    fontSize: "22px",
    margin: "8px 0px 6px",
    fontWeight: "500",
    paddingRight: "20%",
  },

  projSubtitle: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: "14px",
    opacity: 0.7,
    lineHeight: 1.45,
    margin: 0,
  },

  lockedText: {
    marginTop: "16px",
    fontFamily: "'Outfit', sans-serif",
    fontSize: "12px",
    letterSpacing: "2px",
    textTransform: "uppercase",
    opacity: 0.55,
  },
};
